import React from 'react';
import { X, MessageCircle } from 'lucide-react';

/**
 * A minimised conversation, as a bubble in the dock's right-hand column.
 *
 * It used to be individually draggable, with its position saved per chat in localStorage.
 * That reproduced the window bugs in miniature: bubbles scattered down the page, sat on
 * top of app content, survived into viewports where their stored position no longer made
 * sense, and overlapped once two were dragged to the same spot. Nothing here drags any
 * more — `ChatSystem` lays the bubbles out in a fixed column, so they cannot be placed
 * wrongly. (The file keeps its name so existing imports still resolve.)
 */
const DraggableBubble = ({ chat, onMaximize, onClose, unreadCount = 0 }) => {
    const displayName = typeof chat.name === 'string' && chat.name ? chat.name : 'User';
    const initials = displayName.substring(0, 2).toUpperCase();
    const isGroup = chat.type === 'group' || chat.isGroup;
    const badge = Number(unreadCount) || Number(chat.unreadCount) || 0;

    return (
        <div className="relative group">
            <button
                type="button"
                onClick={() => onMaximize(chat.id)}
                title={displayName}
                aria-label={`Open conversation with ${displayName}${badge ? `, ${badge} unread` : ''}`}
                className="block"
            >
                <div className="relative w-14 h-14 rounded-full shadow-xl border-2 border-white overflow-hidden bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center hover:scale-105 transition-transform duration-200">
                    {chat.photo ? (
                        <img
                            src={chat.photo}
                            alt={displayName}
                            className="w-full h-full object-cover"
                            // A dead or expired URL used to leave an empty circle; fall back
                            // to the generated avatar the rest of the app uses.
                            onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=6366f1&color=fff`;
                            }}
                        />
                    ) : (
                        <span className="text-white font-bold text-base select-none">{initials}</span>
                    )}
                </div>
            </button>

            {/* Online dot */}
            {!isGroup && chat.status === 'online' && (
                <div className="absolute bottom-0 right-0 w-4 h-4 bg-emerald-400 rounded-full border-[2.5px] border-white shadow-sm pointer-events-none" />
            )}

            {/* Group marker */}
            {isGroup && (
                <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-indigo-600 rounded-full border-2 border-white flex items-center justify-center shadow-sm pointer-events-none">
                    <MessageCircle size={10} className="text-white" />
                </div>
            )}

            {/* Unread — live from the dock, not the snapshot taken when it was minimised. */}
            {badge > 0 && (
                <div className="absolute -top-1 -right-1 min-w-[22px] h-[22px] bg-red-500 rounded-full flex items-center justify-center border-2 border-white shadow-sm pointer-events-none">
                    <span className="text-white text-[10px] font-bold px-1">{badge > 99 ? '99+' : badge}</span>
                </div>
            )}

            <button
                type="button"
                className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-slate-700 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity hover:bg-red-500 shadow-sm"
                onClick={(e) => { e.stopPropagation(); onClose(chat.id); }}
                title={`Close ${displayName}`}
                aria-label={`Close conversation with ${displayName}`}
            >
                <X size={11} strokeWidth={3} />
            </button>

            {/* Name on hover — a bubble alone does not say who it is. */}
            <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 px-2.5 py-1 bg-slate-900 text-white text-[11px] font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
                {displayName}
            </div>
        </div>
    );
};

export default DraggableBubble;
